import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getModuleForPath, useModulePermissions } from '@/hooks/useModulePermissions';
import { Loader2, Clock } from 'lucide-react';
import { useEffect } from 'react';
import { moduloDoUso, registrarUsoModulo } from '@/lib/usoModulos';
import { ehRotaDoPortalCliente, secaoAbertaAoCliente } from '@/lib/portalClienteRegras';

// Janela de acesso do colaborador em BRT — leitura direta via Intl para não
// depender de fuso da máquina do usuário (evita bloqueios indevidos quando o
// SO está em UTC ou fuso diferente).
const ACCESS_START_MIN = 6 * 60;      // 06:00 BRT
const ACCESS_END_MIN = 19 * 60 + 30;  // 19:30 BRT

function isWithinWorkingHours(): boolean {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  const weekday = get('weekday'); // Mon, Tue, ...
  if (weekday === 'Sat' || weekday === 'Sun') return false;
  const h = parseInt(get('hour'), 10);
  const m = parseInt(get('minute'), 10);
  const total = (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
  return total >= ACCESS_START_MIN && total < ACCESS_END_MIN;
}

function AccessBlockedScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="text-center max-w-sm space-y-4">
        <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center">
          <Clock className="h-8 w-8 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-bold text-foreground">Acesso indisponível</h1>
        <p className="text-sm text-muted-foreground">
          O sistema está disponível para colaboradores de <strong>segunda a sexta</strong>, das <strong>06:00 às 19:30</strong> (horário de Brasília).
        </p>
        <p className="text-xs text-muted-foreground">
          Tente novamente dentro do horário permitido.
        </p>
      </div>
    </div>
  );
}

function ClienteNaoLiberadoScreen({ onSair }: { onSair: () => Promise<void> }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="cliente-nao-liberado">
      <div className="text-center max-w-sm space-y-4">
        <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center">
          <Clock className="h-8 w-8 text-muted-foreground" />
        </div>
        <h1 className="text-xl font-bold text-foreground">Acesso ainda não liberado</h1>
        <p className="text-sm text-muted-foreground">
          O seu acesso ao Portal do Cliente está sendo preparado. Tente de novo mais tarde ou fale com o escritório.
        </p>
        <button type="button" className="text-sm font-medium text-primary-texto underline" onClick={() => void onSair()}>
          Sair
        </button>
      </div>
    </div>
  );
}

// Registra o uso do módulo da tela que de fato abriu: só é montado quando o
// acesso foi liberado, então redirecionamento e bloqueio não contam como uso.
function RegistroDeUso({ pathname }: { pathname: string }) {
  useEffect(() => {
    const modulo = moduloDoUso(pathname);
    if (modulo) registrarUsoModulo(modulo);
  }, [pathname]);
  return null;
}

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin, isColaborador, isLider, isCliente, clienteLiberado, signOut } = useAuth();
  const { hasAccess, loading: permLoading } = useModulePermissions();
  const location = useLocation();
  const moduleKey = getModuleForPath(location.pathname);
  // Na equipe, só o admin abre o Portal do Cliente, como prévia. O cliente
  // passa pela guarda própria, logo depois do login.
  const isAdminOnlyRoute = location.pathname === '/newsletter' || location.pathname === '/admin/carga-setor' || location.pathname === '/admin/painel-gestao' || location.pathname === '/admin/desempenho' || ehRotaDoPortalCliente(location.pathname);





  if (loading || permLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-texto" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Cliente: só o Portal do Cliente, sem janela de horário, e só com o token
  // do papel do cliente (hook ligado). Com token comum ele leria o que
  // qualquer usuário logado lê; aí não entra em tela nenhuma.
  if (isCliente) {
    if (!clienteLiberado) return <ClienteNaoLiberadoScreen onSair={signOut} />;
    if (!ehRotaDoPortalCliente(location.pathname) || !secaoAbertaAoCliente(location.pathname)) {
      return <Navigate to="/portal-cliente" replace />;
    }
    return <>{children}</>;
  }

  const liberado = <><RegistroDeUso pathname={location.pathname} />{children}</>;

  if (isAdminOnlyRoute && !isAdmin) {
    return <Navigate to={isColaborador ? (isLider ? '/portal-lider' : '/portal-colaborador') : '/'} replace />;
  }

  if (location.pathname.startsWith('/portal-lider') && !isLider && !isAdmin) {
    return <Navigate to={isColaborador ? '/portal-colaborador' : '/'} replace />;
  }

  // Admin entra no Portal do Colaborador para avaliar telas, como já faz no Portal
  // do Líder acima. Sem impersonação: as consultas continuam usando o próprio
  // user.id, então telas de dado pessoal (férias, remuneração, tarefas) vêm vazias.
  if (location.pathname.startsWith('/portal-colaborador') && !isColaborador && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Líder: sempre direcionado ao Portal do Líder. Rotas /portal-colaborador/* são redirecionadas
  // para o equivalente em /portal-lider/* (evita que líderes vejam o portal do colaborador).
  if (isLider) {
    if (location.pathname.startsWith('/portal-colaborador')) {
      const liderPath = location.pathname.replace('/portal-colaborador', '/portal-lider');
      return <Navigate to={liderPath} replace />;
    }
    // /acoes não tem módulo: serve a todo usuário logado, como Novidades.
    const isPortalRoute = location.pathname.startsWith('/portal-lider') || location.pathname === '/chat' || location.pathname === '/links' || location.pathname.startsWith('/novidades') || location.pathname === '/desligamentos' || location.pathname === '/acoes';
    if (!isPortalRoute) {
      if (moduleKey && hasAccess(moduleKey)) {
        return liberado;
      }
      return <Navigate to="/portal-lider" replace />;
    }

    // Para rotas do portal-lider com moduleKey mapeado, verificar permissão de módulo
    if (moduleKey && !hasAccess(moduleKey)) {
      return <Navigate to="/portal-lider" replace />;
    }
    return liberado;
  }

  // Colaborador: restrito de segunda a sexta, das 06:00 às 19:30 (ver
  // ACCESS_START_MIN e ACCESS_END_MIN no topo do arquivo).
  if (isColaborador) {
    if (!isWithinWorkingHours()) {
      return <AccessBlockedScreen />;
    }
    const isPortalRoute = location.pathname.startsWith('/portal-colaborador') || location.pathname === '/chat' || location.pathname === '/links' || location.pathname.startsWith('/novidades') || location.pathname === '/desligamentos' || location.pathname === '/acoes';
    if (!isPortalRoute) {
      if (moduleKey && hasAccess(moduleKey)) {
        return liberado;
      }
      return <Navigate to="/portal-colaborador" replace />;
    }
    return liberado;
  }

  if (moduleKey && !hasAccess(moduleKey)) {
    return <Navigate to="/" replace />;
  }

  return liberado;
}
